import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsDownloadButton,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import {
  useErrValidationFailedContext,
  useWriteValidationError,
} from "@/shared/lib/errors/err-validation-failed-context";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { DocumentState } from "@/services/backend/documents/types/state";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { Offer } from "@/services/backend/omni/offer/types/offer";
import {
  useOmniOfferPdfDownloadMutation,
  useOmniOfferShowQuery,
  useOmniOfferUpdateStateMutation,
  useLazyOmniOfferPdfPreviewQuery,
} from "@/services/backend/omni/offer/api/offer-api";

export function OfferPdf({ offer }: { offer: Offer }) {
  const previewRequest = { id: offer.id };
  const [loadPreview, { data, isLoading, isFetching, error }] =
    useLazyOmniOfferPdfPreviewQuery();

  // The number is read from the cache: the caller's copy of the offer may
  // not have picked up an edit of the number yet.
  const { data: cachedOffer } = useOmniOfferShowQuery({ id: offer.id });

  const { write } = useErrValidationFailedContext();
  const { user } = useAuth();

  const onOpenChange = (open: boolean) => {
    if (open) {
      loadPreview(previewRequest);
    }
  };

  return (
    <DocumentSheet onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Angebot")}
              documentNumber={
                <ReferenceNumber
                  referenceNumber={cachedOffer?.referenceNumber}
                />
              }
            />
            <DocumentSheetActionsContent>
              {!isImmutable(offer) && (
                <>
                  <DocumentNotReadyAlert document={t("Angebot")} />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      `Sobald das Angebot festgeschrieben ist, wird das Wasserzeichen "Vorschau" entfernt. Sie können das Angebot jederzeit herunterladen.`,
                    )}
                  />
                  <Separator />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: offer.id,
                      state: DocumentState.Immutable,
                      stateSetBy: user?.id || "",
                    }}
                    mutation={useOmniOfferUpdateStateMutation}
                    description={t(
                      "Das Angebot wird festgeschrieben. Danach kann das Angebot nicht mehr geändert werden.",
                    )}
                    onSuccess={() => loadPreview(previewRequest)}
                    onError={write}
                  />
                </>
              )}
              <DocumentSheetActionsDownload>
                <OfferDownloadButton offerId={offer.id} />
                <OfferDataSheetsDownloadButton offerId={offer.id} />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function OfferDownloadButton({ offerId }: { offerId: string }) {
  const [download, { isLoading, error, data, reset }] =
    useOmniOfferPdfDownloadMutation();

  useDownloadPdf(data, reset);
  useWriteValidationError(error, { onWrite: reset });

  const doDownload = () => {
    if (isLoading) {
      return;
    }
    download({ id: offerId });
  };

  return (
    <>
      <DocumentSheetActionsDownloadButton
        isLoading={isLoading}
        onClick={doDownload}
      >
        {t("Angebot")}
      </DocumentSheetActionsDownloadButton>
      <RTKQueryErrorAlert error={error} />
    </>
  );
}

// OfferDataSheetsDownloadButton downloads the data sheets of the positions
// of the offer alone; the offer PDF already ends with them.
function OfferDataSheetsDownloadButton({ offerId }: { offerId: string }) {
  const [download, { isLoading, error, data, reset }] =
    useOmniOfferPdfDownloadMutation();

  useDownloadPdf(data, reset);

  const doDownload = () => {
    if (isLoading) {
      return;
    }
    download({ id: offerId, document: "data-sheets" });
  };

  return (
    <>
      <DocumentSheetActionsDownloadButton
        isLoading={isLoading}
        onClick={doDownload}
      >
        {t("Datenblätter")}
      </DocumentSheetActionsDownloadButton>
      <RTKQueryErrorAlert error={error} />
    </>
  );
}
